import { Box, Button, Flex, Text, useColorModeValue, useDisclosure, VStack, HStack, FormControl, FormLabel, Textarea, Input, Grid, GridItem, useToast, Center } from '@chakra-ui/react';
import EpisodeCreateModal from 'components/layouts/patients/EpisodeCreateModal';
import SimpleSlider from 'components/ui/Slider';
import Steps, { Step } from 'components/ui/Steps';
import { useState } from 'react';
import CustomModal from 'components/modal/Modal';

const MOCK_PATIENTS = [
  { id: '1', label: 'Juan Carlos Rodríguez',  cedula: '1713175071' },
  { id: '2', label: 'María Fernanda López',    cedula: '0912345678' },
  { id: '3', label: 'Carlos Andrés Mendoza',   cedula: '1756789012' },
];

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface EpisodeData {
  anamnesis: {
    motivo: string;
    evolucion: string;
    sintomas: string;
    intensidadDolor: number;
  };
  signosVitales: {
    peso: number | '';
    talla: number | '';
    temperatura: number | '';
    presionArterial: string;
    frecuenciaCardiaca: number | '';
    frecuenciaRespiratoria: number | '';
  };
  impressionDiagnostica: string;
}

const EPISODE_STEPS: Step[] = [
  { id: '1', title: 'Anamnesis' },
  { id: '2', title: 'Signos Vitales' },
  { id: '3', title: 'Impresión Diagnóstica' },
];

const INITIAL_EPISODE_DATA: EpisodeData = {
  anamnesis: {
    motivo: '',
    evolucion: '',
    sintomas: '',
    intensidadDolor: 5,
  },
  signosVitales: {
    peso: '',
    talla: '',
    temperatura: '',
    presionArterial: '',
    frecuenciaCardiaca: '',
    frecuenciaRespiratoria: '',
  },
  impressionDiagnostica: '',
};

const EpisodeCreatePreview = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [patientId, setPatientId] = useState(MOCK_PATIENTS[0].id);
  
  // Modal Episodio Estados
  const [openSize, setOpenSize] = useState<ModalSize | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [episodeData, setEpisodeData] = useState<EpisodeData>(INITIAL_EPISODE_DATA);
  const toast = useToast();

  const bg         = useColorModeValue('linear-gradient(135deg, #f0f4ff 0%, #faf5ff 40%, #f0fdf4 100%)', 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f2720 100%)');
  const cardBg     = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.100', 'gray.700');
  const titleColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const activeBg   = useColorModeValue('brand.50', 'whiteAlpha.100');
  const activeBorder = useColorModeValue('brand.300', 'brand.600');

  const handleAnamnesisChange = (field: keyof EpisodeData['anamnesis'], value: string | number) => {
    setEpisodeData({
      ...episodeData,
      anamnesis: {
        ...episodeData.anamnesis,
        [field]: value,
      },
    });
  };

  const handleSignosVitalesChange = (
    field: keyof EpisodeData['signosVitales'],
    value: string | number
  ) => {
    setEpisodeData({
      ...episodeData,
      signosVitales: {
        ...episodeData.signosVitales,
        [field]: field === 'presionArterial' ? value : value === '' ? '' : Number(value),
      },
    });
  };

  const handleNextStep = () => {
    if (currentStep < EPISODE_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateEpisode = () => {
    console.log('Nuevo Episodio Clínico:', episodeData);
    toast({
      title: 'Episodio creado',
      description: 'El episodio clínico ha sido creado exitosamente',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    resetModal();
  };

  const resetModal = () => {
    setOpenSize(null);
    setCurrentStep(0);
    setEpisodeData(INITIAL_EPISODE_DATA);
  };

  const isLastStep = currentStep === EPISODE_STEPS.length - 1;

  return (
    <Box minH="100vh" background={bg} p="40px">
      <Box maxW="560px" mx="auto">

        <Box mb="32px">
          <Text fontSize="2xl" fontWeight="800" color={titleColor}>EpisodeCreateModal</Text>
          <Text fontSize="sm" color={mutedColor} mt="4px">
            Wizard de 3 pasos para apertura de episodio clínico con EVA Pain Scale.
          </Text>
        </Box>

        <Box bg={cardBg} borderRadius="12px" p="24px" border="1px solid" borderColor={cardBorder}>
          <Text fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase"
            letterSpacing="0.08em" mb="14px">
            Seleccionar paciente mock
          </Text>

          <Flex direction="column" gap="8px" mb="24px">
            {MOCK_PATIENTS.map((p) => (
              <Flex
                key={p.id}
                align="center"
                justify="space-between"
                px="14px"
                py="10px"
                borderRadius="8px"
                border="1px solid"
                borderColor={patientId === p.id ? activeBorder : cardBorder}
                bg={patientId === p.id ? activeBg : 'transparent'}
                cursor="pointer"
                transition="all 0.12s"
                onClick={() => setPatientId(p.id)}
              >
                <Box>
                  <Text fontSize="sm" fontWeight="600" color={titleColor}>{p.label}</Text>
                  <Text fontSize="xs" color={mutedColor}>{p.cedula}</Text>
                </Box>
                {patientId === p.id && (
                  <Box w="8px" h="8px" borderRadius="full" bg="brand.500" flexShrink={0} />
                )}
              </Flex>
            ))}
          </Flex>

          <Button w="full" onClick={() => setOpenSize('lg')}>
            Abrir modal — Nuevo Episodio (Preview)
          </Button>
          
          <Button w="full" mt="3" colorScheme="purple" onClick={onOpen}>
            Abrir modal — Nuevo Episodio (Actual)
          </Button>
        </Box>

      </Box>

      {/* MODAL PREVIEW CON 3 STEPS */}
      <CustomModal
        isOpen={openSize !== null}
        onClose={resetModal}
        title='Nuevo Episodio Clínico'
        size={openSize ?? 'md'}
        primaryBtnLabel={isLastStep ? 'Crear Episodio' : 'Siguiente'}
        onPrimaryClick={isLastStep ? handleCreateEpisode : handleNextStep}
        headerExtra={
          <Steps steps={EPISODE_STEPS} activeStep={currentStep} size='sm' labelPlacement='bottom' />
        }
      >
        <VStack spacing='20px' align='stretch'>
          {/* STEP 1: ANAMNESIS */}
          {currentStep === 0 && (
            <>
              <FormControl isRequired>
                <FormLabel fontSize='sm' fontWeight='600'>Motivo de Consulta</FormLabel>
                <Textarea
                  placeholder='Describe el motivo principal de consulta...'
                  rows={3}
                  resize='none'
                  fontSize='sm'
                  value={episodeData.anamnesis.motivo}
                  onChange={(e) => handleAnamnesisChange('motivo', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize='sm' fontWeight='600'>Evolución</FormLabel>
                <Textarea
                  placeholder='Describe la evolución del problema (ej: tiempo desde el inicio)...'
                  rows={3}
                  resize='none'
                  fontSize='sm'
                  value={episodeData.anamnesis.evolucion}
                  onChange={(e) => handleAnamnesisChange('evolucion', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize='sm' fontWeight='600'>Síntomas</FormLabel>
                <Textarea
                  placeholder='Describe los síntomas principales...'
                  rows={2}
                  resize='none'
                  fontSize='sm'
                  value={episodeData.anamnesis.sintomas}
                  onChange={(e) => handleAnamnesisChange('sintomas', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize='sm' fontWeight='600'>Intensidad de Dolor (EVA)</FormLabel>
                <Box pt='4'>
                  <SimpleSlider
                    value={episodeData.anamnesis.intensidadDolor}
                    onChange={(value) => handleAnamnesisChange('intensidadDolor', value)}
                    min={0}
                    max={10}
                    step={1}
                    emojis={['😊', '🙂', '🙂', '😐', '😐', '😕', '😕', '😢', '😢', '😭', '😭']}
                    label="Nivel de Dolor"
                    showValue={true}
                    size="md"
                  />
                </Box>
              </FormControl>
            </>
          )}

          {/* STEP 2: SIGNOS VITALES */}
          {currentStep === 1 && (
            <>
              <Grid templateColumns='repeat(2, 1fr)' gap='4'>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Peso (kg)</FormLabel>
                    <Input
                      type='number'
                      placeholder='Ej: 74.5'
                      fontSize='sm'
                      step='0.1'
                      value={episodeData.signosVitales.peso}
                      onChange={(e) => handleSignosVitalesChange('peso', e.target.value)}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Talla (m)</FormLabel>
                    <Input
                      type='number'
                      placeholder='Ej: 1.72'
                      fontSize='sm'
                      step='0.01'
                      value={episodeData.signosVitales.talla}
                      onChange={(e) => handleSignosVitalesChange('talla', e.target.value)}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Temperatura (°C)</FormLabel>
                    <Input
                      type='number'
                      placeholder='Ej: 36.6'
                      fontSize='sm'
                      step='0.1'
                      value={episodeData.signosVitales.temperatura}
                      onChange={(e) => handleSignosVitalesChange('temperatura', e.target.value)}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Presión Arterial</FormLabel>
                    <Input
                      type='text'
                      placeholder='Ej: 120/80'
                      fontSize='sm'
                      value={episodeData.signosVitales.presionArterial}
                      onChange={(e) => handleSignosVitalesChange('presionArterial', e.target.value)}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Frecuencia Cardíaca</FormLabel>
                    <Input
                      type='number'
                      placeholder='Ej: 72'
                      fontSize='sm'
                      value={episodeData.signosVitales.frecuenciaCardiaca}
                      onChange={(e) => handleSignosVitalesChange('frecuenciaCardiaca', e.target.value)}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='600'>Frecuencia Respiratoria</FormLabel>
                    <Input
                      type='number'
                      placeholder='Ej: 16'
                      fontSize='sm'
                      value={episodeData.signosVitales.frecuenciaRespiratoria}
                      onChange={(e) => handleSignosVitalesChange('frecuenciaRespiratoria', e.target.value)}
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </>
          )}

          {/* STEP 3: IMPRESION DIAGNOSTICA */}
          {currentStep === 2 && (
            <>
              <FormControl isRequired>
                <FormLabel fontSize='sm' fontWeight='600'>Impresión Diagnóstica</FormLabel>
                <Textarea
                  placeholder='Describe la impresión diagnóstica basado en la evaluación...'
                  rows={6}
                  resize='none'
                  fontSize='sm'
                  value={episodeData.impressionDiagnostica}
                  onChange={(e) =>
                    setEpisodeData({
                      ...episodeData,
                      impressionDiagnostica: e.target.value,
                    })
                  }
                />
              </FormControl>
            </>
          )}

          {/* NAVIGATION BUTTONS */}
          <HStack spacing='3' justify='space-between' pt='4'>
            <Button
              size='sm'
              variant='ghost'
              onClick={handlePrevStep}
              isDisabled={currentStep === 0}
            >
              Anterior
            </Button>
            <Text fontSize='xs' color='gray.500'>
              Paso {currentStep + 1} de {EPISODE_STEPS.length}
            </Text>
          </HStack>
        </VStack>
      </CustomModal>

      {/* MODAL ORIGINAL */}
      <EpisodeCreateModal
        isOpen={isOpen}
        onClose={onClose}
        patientId={patientId}
      />
    </Box>
  );
};

export default EpisodeCreatePreview;
